import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipLoader } from 'react-spinners';
import { FiSearch, FiImage, FiCopy, FiCheck } from "react-icons/fi";

const ImagesSection = ({ onImageSelect }) => { // Receive onImageSelect as a prop
  const [searchQuery, setSearchQuery] = useState("");
  const [images, setImages] = useState([]); // To store images fetched
  const [copiedId, setCopiedId] = useState(null); // To track copied image
  const [photoUrls, setPhotoUrls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCopy = (imageUrl) => {
    // Instead of copying to clipboard, directly trigger handleImageClick
    handleImageClick(imageUrl);
    setCopiedId(imageUrl); //setCopiedID is still needed for visual purposes.
    setTimeout(() => setCopiedId(null), 2000);  //To revert button
  };

  const fetchImages = async () => {
    if (!searchQuery) return;  // Don't fetch if search is empty

    setIsLoading(true);
    setError(null);  // Clear previous error

    try {
      const response = await axios.get(`https://trendhopper-c3934b7293e6.herokuapp.com/api/photos/`, {
        params: {
          query: searchQuery,
          per_page: 6, // Specify number of images you want per request
        },
      });

      setPhotoUrls(response.data.photos);  // Set the image URLs returned by the backend
    } catch (err) {
      setError("Error fetching photos.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      const timeout = setTimeout(() => {
        fetchImages();
      }, 500); // Delay search request to avoid too many API calls while typing

      return () => clearTimeout(timeout); // Cleanup timeout on unmount or query change
    }
  }, [searchQuery]); // Run the effect when the search query changes

  // Function to handle image selection
  const handleImageClick = (imageUrl) => {
    const imageHtml = `<img src="${imageUrl}" alt="Selected Image" style="max-width: 100%; height: auto;" />`;
    console.log("handleImageClick called with:", imageHtml); // ADD THIS
    onImageSelect(imageHtml);  // Call the onImageSelect function with the HTML
};

  return (
    <div className="space-y-4 h-full bg-white p-4">
      <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-800">
        <FiImage className="text-blue-500" />
        Images
      </h2>

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FiSearch className="text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search images..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full pl-10 p-2.5 bg-white border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Loading Spinner */}
      {isLoading ? (
        <div className="flex justify-center items-center mt-4">
          <ClipLoader color="#36D7B7" size={50} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 overflow-y-auto max-h-[calc(100vh-150px)] pb-4">
          {photoUrls.length > 0 ? (
            photoUrls.map((image, index) => (
              <div
                key={index}
                className="border border-gray-300 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all bg-white group"
              >
                <div className="relative">
                  <div className="w-full aspect-video bg-gray-200 flex justify-center items-center overflow-hidden">
                    {/* Directly use the image URL */}
                    <img
                      src={image} // image is the URL itself
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                      onClick={() => handleImageClick(image)} // Attach the click handler
                      style={{ cursor: 'pointer' }} // Make it clear it's clickable
                    />
                  </div>

                  {/* Copy button */}
                  <button
                    onClick={() => handleCopy(image)} // Handle copying image URL
                    className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Copy image"
                  >
                    {copiedId === image ? (
                      <FiCheck className="text-green-500" />
                    ) : (
                      <FiCopy className="text-gray-700" />
                    )}
                  </button>
                </div>

                {/* Image Info */}
                <div className="p-2 border-t border-gray-300 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-800">{`Image ${index + 1}`}</span>
                  <span className="text-xs text-gray-500">Click to copy</span>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-500 space-y-4">
            {/* Large Icon */}
            <FiImage className="text-gray-400 text-6xl" />

            {/* Descriptive Text */}
            <p className="text-lg font-medium">No images found</p>
            <p className="text-sm text-center">
                Try searching with different keywords or check back later for new results.
            </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImagesSection;